import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { 
  Calendar, MapPin, Clock, Users, ArrowLeft, Share2, Heart, 
  Check, ExternalLink, MessageCircle, Ticket, User, ChevronRight,
  Home, Edit2, Save, X, Loader2, Plus, Minus, Trash2, ChevronLeft,
  Play, Pause, Volume2, VolumeX, Maximize, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth, useCurrency, API, useLanguage } from '../App';
import { useSmartBack } from '../components/ui/Breadcrumbs';

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { formatPrice } = useCurrency();
  const { language } = useLanguage();
  const handleBack = useSmartBack('/events'); // Fallback to events page
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [showManageTickets, setShowManageTickets] = useState(false);
  const [editingTickets, setEditingTickets] = useState([]);
  const [savingTickets, setSavingTickets] = useState(false);
  
  // Image gallery state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  
  // Video player state
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const isOwner = user && event && user.id === event.user_id;

  // Get all images (prioritize new images array, fallback to legacy image)
  const getEventImages = () => {
    if (event?.images && event.images.length > 0) {
      return event.images;
    }
    if (event?.image) {
      return [event.image];
    }
    return [];
  };

  const eventImages = event ? getEventImages() : [];
  const eventVideos = event?.videos || [];

  useEffect(() => {
    fetchEvent();
    fetchCategories();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}`);
      setEvent(res.data);
    } catch (error) {
      console.error('Failed to fetch event:', error);
      toast.error('Event not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/events/categories`);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleRSVP = async (status) => {
    if (!user) {
      toast.error('Please sign in to RSVP');
      navigate('/auth');
      return;
    }

    // Use the actual event ID, not the URL slug
    const actualEventId = event?.id;
    if (!actualEventId) {
      toast.error('Event not loaded');
      return;
    }

    setRsvpLoading(true);
    try {
      if (event.user_rsvp === status) {
        await axios.delete(`${API}/events/${actualEventId}/rsvp`);
        toast.success('RSVP removed');
      } else {
        await axios.post(`${API}/events/${actualEventId}/rsvp?status=${status}`);
        toast.success(status === 'going' ? "You're going!" : "Marked as interested");
      }
      fetchEvent();
    } catch (error) {
      toast.error('Failed to update RSVP');
    } finally {
      setRsvpLoading(false);
    }
  };

  // Ticket management functions (for event owner)
  const openTicketManager = () => {
    setEditingTickets(event.tickets ? JSON.parse(JSON.stringify(event.tickets)) : []);
    setShowManageTickets(true);
  };

  const updateTicketField = (index, field, value) => {
    const updated = [...editingTickets];
    updated[index] = { ...updated[index], [field]: value };
    setEditingTickets(updated);
  };

  const addNewTicketType = () => {
    setEditingTickets([...editingTickets, { name: '', price: 0, quantity: 50, sold: 0, description: '' }]);
  };

  const removeTicketType = (index) => {
    if (editingTickets.length > 1) {
      setEditingTickets(editingTickets.filter((_, i) => i !== index));
    }
  };

  const saveTickets = async () => {
    for (const ticket of editingTickets) {
      if (!ticket.name) {
        toast.error('All tickets must have a name');
        return;
      }
      if (ticket.quantity < (ticket.sold || 0)) {
        toast.error(`Cannot set quantity below sold amount for "${ticket.name}"`);
        return;
      }
    }

    setSavingTickets(true);
    try {
      await axios.put(`${API}/events/${eventId}/tickets`, editingTickets);
      toast.success('Tickets updated successfully');
      setShowManageTickets(false);
      fetchEvent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tickets');
    } finally {
      setSavingTickets(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: `Check out this event: ${event.title}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  const openWhatsApp = () => {
    if (event.organizer_whatsapp) {
      const phone = event.organizer_whatsapp.replace(/\D/g, '');
      const message = encodeURIComponent(`Hi! I'm interested in your event: ${event.title}`);
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    }
  };

  // Image navigation
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % eventImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + eventImages.length) % eventImages.length);
  };

  // Toggle ticket stats visibility (for owner)
  const toggleTicketStatsVisibility = async () => {
    try {
      await axios.put(`${API}/events/${eventId}`, 
        { show_ticket_stats: !event.show_ticket_stats });
      toast.success(event.show_ticket_stats ? 'Ticket stats hidden from public' : 'Ticket stats now visible');
      fetchEvent();
    } catch (error) {
      toast.error('Failed to update visibility setting');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Event not found</h1>
          <Link to="/events" className="text-primary hover:underline">Back to events</Link>
        </div>
      </div>
    );
  }

  const category = categories.find(c => c.id === event.category);
  const eventDate = new Date(event.event_date);
  const isPast = eventDate < new Date();
  const showTicketStats = event.show_ticket_stats !== false; // Default to true if not set

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto px-4 sm:px-6 py-6 md:py-8 max-w-6xl">
        {/* Breadcrumbs */}
        <div className="flex flex-col gap-3 mb-6">
          <button
            onClick={handleBack}
            className="flex items-center text-muted-foreground hover:text-primary transition-colors w-fit"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {language === 'nl' ? 'Terug' : language === 'es' ? 'Volver' : 'Back'}
          </button>
          
          <nav className="flex items-center text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary flex items-center gap-1">
              <Home className="w-4 h-4" />
            </Link>
            <ChevronRight className="w-4 h-4 mx-1.5" />
            <Link to="/events" className="hover:text-primary">Events</Link>
            <ChevronRight className="w-4 h-4 mx-1.5" />
            <span className="text-foreground truncate max-w-[200px]">{event.title}</span>
          </nav>
        </div>

        {/* Owner Controls */}
        {isOwner && (
          <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-xl">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-semibold text-foreground">You're the organizer</h3>
                <p className="text-sm text-muted-foreground">Manage your event settings and tickets</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {event.tickets && event.tickets.length > 0 && (
                  <>
                    <button
                      onClick={toggleTicketStatsVisibility}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                        showTicketStats 
                          ? 'bg-secondary text-foreground hover:bg-secondary/80' 
                          : 'bg-amber-500/20 text-amber-600 hover:bg-amber-500/30'
                      }`}
                      title={showTicketStats ? 'Hide ticket stats from public' : 'Show ticket stats to public'}
                    >
                      {showTicketStats ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {showTicketStats ? 'Stats Visible' : 'Stats Hidden'}
                    </button>
                    <button
                      onClick={openTicketManager}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      <Ticket className="w-4 h-4" />
                      Manage Tickets
                    </button>
                  </>
                )}
                <Link
                  to={`/events/${eventId}/edit`}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Event
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Image Gallery */}
        <div className="relative rounded-2xl overflow-hidden bg-secondary mb-6">
          {eventImages.length > 0 ? (
            <div className="relative">
              {/* Main Image */}
              <div 
                className="aspect-[21/9] cursor-pointer"
                onClick={() => setShowLightbox(true)}
              >
                <img 
                  src={eventImages[currentImageIndex]} 
                  alt={`${event.title} - Image ${currentImageIndex + 1}`} 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Navigation Arrows */}
              {eventImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  
                  {/* Image Counter */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 text-white text-sm rounded-full">
                    {currentImageIndex + 1} / {eventImages.length}
                  </div>
                </>
              )}
              
              {/* Thumbnail Strip */}
              {eventImages.length > 1 && (
                <div className="absolute bottom-3 right-3 flex gap-1.5">
                  {eventImages.slice(0, 5).map((img, index) => (
                    <button
                      key={index}
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                      className={`w-12 h-8 rounded overflow-hidden border-2 transition-all ${
                        index === currentImageIndex ? 'border-white scale-110' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                  {eventImages.length > 5 && (
                    <div className="w-12 h-8 rounded bg-black/60 flex items-center justify-center text-white text-xs font-medium">
                      +{eventImages.length - 5}
                    </div>
                  )}
                </div>
              )}
              
              {isPast && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center pointer-events-none">
                  <span className="text-white text-2xl font-bold">Past Event</span>
                </div>
              )}
            </div>
          ) : (
            <div 
              className="aspect-[21/9] flex items-center justify-center text-8xl"
              style={{ backgroundColor: category?.color + '20' }}
            >
              {category?.icon || '📅'}
              {isPast && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">Past Event</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Video Section */}
        {eventVideos.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Play className="w-5 h-5 text-primary" />
              Event Videos
            </h3>
            <div className="space-y-4">
              {/* Active Video Player */}
              <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                <video
                  key={eventVideos[activeVideoIndex]}
                  src={eventVideos[activeVideoIndex]}
                  controls
                  className="w-full h-full"
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  onEnded={() => setIsVideoPlaying(false)}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              
              {/* Video Thumbnails (if multiple) */}
              {eventVideos.length > 1 && (
                <div className="flex gap-3">
                  {eventVideos.map((video, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveVideoIndex(index)}
                      className={`relative w-32 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        index === activeVideoIndex 
                          ? 'border-primary ring-2 ring-primary/30' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <video src={video} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <Play className="w-6 h-6 text-white" />
                      </div>
                      <span className="absolute bottom-1 left-1 text-xs text-white bg-black/60 px-1.5 rounded">
                        Video {index + 1}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== EVENT HEADER: Full Width Above Grid ===== */}
        <div className="mb-8 border-b border-border pb-6">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span 
              className="px-3 py-1 rounded-full text-sm font-medium"
              style={{ backgroundColor: category?.color + '20', color: category?.color }}
            >
              {category?.icon} {category?.name || event.category}
            </span>
            {(() => {
              const minTicketPrice = event.tickets && event.tickets.length > 0
                ? Math.min(...event.tickets.map(t => t.price || 0))
                : null;
              const displayPrice = minTicketPrice !== null ? minTicketPrice : event.price;
              const isFree = displayPrice === 0 || displayPrice === null;
              
              return isFree ? (
                <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm font-medium">
                  Free Event
                </span>
              ) : (
                <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium">
                  {event.tickets && event.tickets.length > 1 ? 'From ' : ''}
                  {formatPrice(displayPrice, event.price_currency || 'USD')}
                </span>
              );
            })()}
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-foreground mb-4 tracking-tight leading-tight">
            {event.title}
          </h1>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center font-bold text-primary border border-border">
              {event.user_name?.charAt(0) || 'A'}
            </div>
            <p className="text-muted-foreground">
              Organized by <span className="font-semibold text-foreground">{event.user_name}</span>
            </p>
          </div>
        </div>

        {/* ===== MAIN GRID: Content + Sidebar ===== */}
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Date & Time */}
            <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-lg">Date & Time</h3>
                  <p className="text-muted-foreground mt-1">{formatDateTime(event.event_date)}</p>
                  {event.end_date && (
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Until {formatDateTime(event.end_date)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground text-lg">Location</h3>
                  {event.location?.venue_name && (
                    <p className="font-medium text-foreground mt-1">{event.location.venue_name}</p>
                  )}
                  <p className="text-muted-foreground">{event.location?.address}</p>
                  <p className="text-sm text-muted-foreground capitalize">
                    {event.location?.city && `${event.location.city}, `}{event.location?.island}
                  </p>
                  
                  {event.location?.latitude && event.location?.longitude && (
                    <a
                      href={`https://maps.google.com/?q=${event.location.latitude},${event.location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline mt-2 text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open in Google Maps
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
              <h3 className="font-semibold text-foreground text-lg mb-3">About this event</h3>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">{event.description}</p>
              </div>
            </div>

            {/* Tickets Section */}
            {event.tickets && event.tickets.length > 0 && (
              <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
                <h3 className="font-semibold text-foreground text-lg mb-4 flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-primary" />
                  Tickets
                </h3>
                <div className="space-y-3">
                  {event.tickets.map((ticket, index) => {
                    const remaining = ticket.quantity - (ticket.sold || 0);
                    const soldOut = remaining <= 0;
                    const lowStock = remaining > 0 && remaining <= 10;
                    
                    return (
                      <div 
                        key={index}
                        className={`p-4 rounded-xl border ${
                          soldOut 
                            ? 'bg-secondary/50 border-border opacity-60' 
                            : lowStock 
                              ? 'bg-amber-500/5 border-amber-500/30' 
                              : 'bg-secondary/30 border-border'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-foreground">{ticket.name}</span>
                          <span className="font-bold text-primary">
                            {ticket.price === 0 ? 'Free' : formatPrice(ticket.price, 'USD')}
                          </span>
                        </div>
                        {ticket.description && (
                          <p className="text-sm text-muted-foreground mb-2">{ticket.description}</p>
                        )}
                        {/* Only show stats if showTicketStats is true OR user is owner */}
                        {(showTicketStats || isOwner) && (
                          <div className="flex items-center justify-between">
                            <span className={`text-sm ${
                              soldOut 
                                ? 'text-red-500 font-medium' 
                                : lowStock 
                                  ? 'text-amber-500 font-medium' 
                                  : 'text-muted-foreground'
                            }`}>
                              {soldOut 
                                ? 'Sold Out' 
                                : lowStock 
                                  ? `Only ${remaining} left!` 
                                  : `${remaining} available`}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {ticket.sold || 0} / {ticket.quantity} sold
                            </span>
                          </div>
                        )}
                        {/* Show "Available" badge when stats are hidden */}
                        {!showTicketStats && !isOwner && !soldOut && (
                          <span className="text-sm text-green-500 font-medium">Available</span>
                        )}
                        {!showTicketStats && !isOwner && soldOut && (
                          <span className="text-sm text-red-500 font-medium">Sold Out</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Total capacity summary - only for owner or when stats visible */}
                {(showTicketStats || isOwner) && (
                  <div className="mt-4 pt-3 border-t border-border">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total tickets sold:</span>
                      <span className="font-medium text-foreground">
                        {event.tickets.reduce((sum, t) => sum + (t.sold || 0), 0)} / {event.tickets.reduce((sum, t) => sum + t.quantity, 0)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* RSVP Card */}
            {!isPast && (
              <div className="bg-gradient-to-br from-card to-secondary/30 rounded-2xl border border-border p-6 shadow-lg sticky top-20">
                <h3 className="font-bold text-foreground text-lg mb-4 text-center">Are you going?</h3>
                
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <button
                    onClick={() => handleRSVP('interested')}
                    disabled={rsvpLoading}
                    className={`py-3 rounded-xl font-semibold transition-all flex flex-col items-center justify-center gap-1.5 ${
                      event.user_rsvp === 'interested'
                        ? 'bg-gradient-to-br from-pink-500 to-pink-600 text-white shadow-lg shadow-pink-500/30'
                        : 'bg-secondary hover:bg-pink-500/10 text-foreground border border-border'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${event.user_rsvp === 'interested' ? 'fill-current' : ''}`} />
                    <span className="text-sm">Interested</span>
                  </button>
                  <button
                    onClick={() => handleRSVP('going')}
                    disabled={rsvpLoading}
                    className={`py-3 rounded-xl font-semibold transition-all flex flex-col items-center justify-center gap-1.5 ${
                      event.user_rsvp === 'going'
                        ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/30'
                        : 'bg-secondary hover:bg-green-500/10 text-foreground border border-border'
                    }`}
                  >
                    <Check className="w-5 h-5" />
                    <span className="text-sm">Going</span>
                  </button>
                </div>

                {/* Stats */}
                <div className="flex justify-around py-4 bg-secondary/50 rounded-xl mb-5">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-pink-500">{event.interested_count || 0}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Interested</div>
                  </div>
                  <div className="w-px bg-border" />
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-500">{event.going_count || 0}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Going</div>
                  </div>
                </div>

                {/* Contact Organizer */}
                {event.organizer_whatsapp && (
                  <button
                    onClick={openWhatsApp}
                    className="w-full py-3 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-xl font-semibold flex items-center justify-center gap-2 mb-2 shadow-md transition-all"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Contact via WhatsApp
                  </button>
                )}

                {/* Free Event Badge */}
                {event.is_free && (
                  <div className="w-full py-3 bg-green-500/20 text-green-500 rounded-xl font-semibold flex items-center justify-center gap-2 mb-2 border border-green-500/30">
                    <Check className="w-5 h-5" />
                    Free Event
                  </div>
                )}

                {/* Get Tickets - only show if not free and has external_link */}
                {!event.is_free && event.external_link && (
                  <a
                    href={event.external_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl font-semibold flex items-center justify-center gap-2 mb-2 shadow-md transition-all block"
                  >
                    <Ticket className="w-5 h-5" />
                    Get Tickets
                  </a>
                )}

                {/* More Info Button */}
                {event.info_url && (
                  <a
                    href={event.info_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-500 rounded-xl font-semibold flex items-center justify-center gap-2 mb-2 border border-blue-500/30 transition-all block"
                  >
                    <ExternalLink className="w-5 h-5" />
                    More Info
                  </a>
                )}

                {/* Social Links */}
                {(event.facebook_url || event.instagram_url) && (
                  <div className="flex gap-2 mb-2">
                    {event.facebook_url && (
                      <a
                        href={event.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 py-3 bg-[#1877F2]/20 hover:bg-[#1877F2]/30 text-[#1877F2] rounded-xl font-semibold flex items-center justify-center gap-2 border border-[#1877F2]/30 transition-all"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        Facebook
                      </a>
                    )}
                    {event.instagram_url && (
                      <a
                        href={event.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 py-3 bg-[#E4405F]/20 hover:bg-[#E4405F]/30 text-[#E4405F] rounded-xl font-semibold flex items-center justify-center gap-2 border border-[#E4405F]/30 transition-all"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                        </svg>
                        Instagram
                      </a>
                    )}
                  </div>
                )}

                <button
                  onClick={handleShare}
                  className="w-full py-3 bg-secondary hover:bg-secondary/80 text-foreground rounded-xl font-medium flex items-center justify-center gap-2 border border-border transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  Share Event
                </button>
              </div>
            )}

            {/* Past Event Notice */}
            {isPast && (
              <div className="bg-card rounded-2xl border border-border p-5 text-center">
                <div className="w-14 h-14 bg-secondary rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-7 h-7 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">This event has ended</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Check out upcoming events in your area
                </p>
                <Link
                  to="/events"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Browse Events
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {showLightbox && eventImages.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/95 z-[150] flex items-center justify-center"
          onClick={() => setShowLightbox(false)}
        >
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <img
            src={eventImages[currentImageIndex]}
            alt={`${event.title} - Image ${currentImageIndex + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {eventImages.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Ticket Management Modal */}
      {showManageTickets && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[150] flex items-center justify-center p-4" onClick={() => setShowManageTickets(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-border" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                Manage Tickets
              </h3>
              <button 
                onClick={() => setShowManageTickets(false)} 
                className="p-2 hover:bg-secondary rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {editingTickets.map((ticket, index) => (
                <div key={index} className="p-4 bg-secondary/50 rounded-xl border border-border">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-foreground">Ticket Type #{index + 1}</span>
                    {editingTickets.length > 1 && (
                      <button
                        onClick={() => removeTicketType(index)}
                        className="p-1.5 text-red-500 hover:bg-red-500/10 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Ticket Name</label>
                      <input
                        type="text"
                        value={ticket.name}
                        onChange={(e) => updateTicketField(index, 'name', e.target.value)}
                        placeholder="e.g., VIP, Basic"
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Price ($)</label>
                      <input
                        type="number"
                        value={ticket.price}
                        onChange={(e) => updateTicketField(index, 'price', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Total Quantity</label>
                      <input
                        type="number"
                        value={ticket.quantity}
                        onChange={(e) => updateTicketField(index, 'quantity', parseInt(e.target.value) || 0)}
                        min={ticket.sold || 0}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Already Sold</label>
                      <input
                        type="number"
                        value={ticket.sold || 0}
                        onChange={(e) => updateTicketField(index, 'sold', Math.min(parseInt(e.target.value) || 0, ticket.quantity))}
                        min="0"
                        max={ticket.quantity}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Description (optional)</label>
                    <input
                      type="text"
                      value={ticket.description || ''}
                      onChange={(e) => updateTicketField(index, 'description', e.target.value)}
                      placeholder="e.g., Includes backstage access"
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm"
                    />
                  </div>

                  {/* Stats */}
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Remaining: <span className="font-medium text-foreground">{ticket.quantity - (ticket.sold || 0)}</span>
                    </span>
                    <span className={`font-medium ${
                      (ticket.quantity - (ticket.sold || 0)) <= 0 
                        ? 'text-red-500' 
                        : (ticket.quantity - (ticket.sold || 0)) <= 10 
                          ? 'text-amber-500' 
                          : 'text-green-500'
                    }`}>
                      {(ticket.quantity - (ticket.sold || 0)) <= 0 
                        ? 'Sold Out' 
                        : (ticket.quantity - (ticket.sold || 0)) <= 10 
                          ? 'Low Stock' 
                          : 'Available'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addNewTicketType}
              className="w-full py-2 border-2 border-dashed border-border rounded-xl text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Another Ticket Type
            </button>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowManageTickets(false)}
                className="flex-1 py-3 bg-secondary text-foreground rounded-xl font-medium hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveTickets}
                disabled={savingTickets}
                className="flex-1 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingTickets ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-5 h-5" /> Save Changes</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default EventDetailPage;
